// Opens a document that lives in the private R2 bucket.
//
// Rows store an R2 object *key*, not a public URL — the bucket isn't public, so
// there's no href we can put in an <a> directly. Clicking here trades the key
// for a short-lived presigned URL via /api/documents/signed-url (Clerk-authed,
// tenant-scoped) and opens that.
//
// Legacy rows written while a public base URL was configured still have a URL;
// when `fallbackUrl` is present we just render a plain link to it.

import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { FileText } from 'lucide-react';
import { api } from '@/lib/api';

interface SignedUrlResponse {
  url: string;
  expiresInSec: number;
}

interface Props {
  /** R2 object key (canonical). */
  objectKey: string | null;
  /** Legacy public URL for rows uploaded before the private-bucket switch. */
  fallbackUrl?: string | null;
  /** Link text — usually the original filename. */
  label: string;
  /** Original upload filename — passed to the signer so the saved file keeps
   *  its real name instead of the R2 key. Defaults to `label` when it looks
   *  like a filename. */
  filename?: string | null;
  /** Tooltip (callers pass the SHA-256 for audit). */
  title?: string;
  className?: string;
  /** Render the file icon before the label. */
  showIcon?: boolean;
}

export function DocumentLink({
  objectKey,
  fallbackUrl,
  label,
  filename,
  title,
  className,
  showIcon = false,
}: Props) {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const linkClass = className ?? 'text-primary hover:underline font-medium';

  async function open() {
    if (!objectKey || loading) return;
    setError(null);
    setLoading(true);

    // Open the tab synchronously — browsers block window.open() once it's behind
    // an await, so we claim the tab first and point it at the URL when it lands.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;

    try {
      const name = filename ?? (label.includes('.') ? label : null);
      const res = await api<SignedUrlResponse>(
        `/api/documents/signed-url?key=${encodeURIComponent(objectKey)}` +
          (name ? `&filename=${encodeURIComponent(name)}` : ''),
        { method: 'GET', tokenGetter: () => getToken() },
      );
      if (tab) tab.location.href = res.url;
      else window.location.href = res.url; // popup blocked — navigate in place
    } catch (err) {
      tab?.close();
      setError((err as { message?: string })?.message ?? 'Could not open the document.');
    } finally {
      setLoading(false);
    }
  }

  // Legacy public-URL rows: a normal link is all that's needed.
  if (!objectKey && fallbackUrl) {
    return (
      <a
        href={fallbackUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClass}
        title={title}
      >
        {showIcon && <FileText className="w-3.5 h-3.5 inline mr-1 -mt-0.5" aria-hidden />}
        {label}
      </a>
    );
  }

  if (!objectKey) return null;

  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1">
      <button
        type="button"
        onClick={open}
        disabled={loading}
        className={`${linkClass} text-left disabled:opacity-60`}
        title={title}
      >
        {showIcon && <FileText className="w-3.5 h-3.5 inline mr-1 -mt-0.5" aria-hidden />}
        {loading ? 'Opening…' : label}
      </button>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </span>
  );
}
